import React from 'react';
import { FaClock, FaSpinner, FaTruck, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';

export type OrderStatus = 'pending' | 'preparing' | 'on-the-way' | 'completed' | 'cancelled';

export interface OrderStatusConfig {
  text: string;
  icon: React.ComponentType<any>;
  color: string;
  bgClass: string;
  simpleClass: string;
  buttonActiveClass: string;
  buttonInactiveClass: string;
}

export const ORDER_STATUS_CONFIG: Record<OrderStatus, OrderStatusConfig> = {
  pending: {
    text: 'Bekliyor',
    icon: FaClock,
    color: 'yellow',
    bgClass: 'bg-gradient-to-r from-yellow-100 to-yellow-200 text-yellow-800 border-yellow-300',
    simpleClass: 'bg-yellow-100 text-yellow-800',
    buttonActiveClass:
      'bg-gradient-to-r from-yellow-500 to-yellow-600 text-white border-yellow-300 shadow-md',
    buttonInactiveClass:
      'bg-white border-yellow-200 text-yellow-700 hover:border-yellow-300 hover:bg-yellow-50',
  },
  preparing: {
    text: 'Hazırlanıyor',
    icon: FaSpinner,
    color: 'blue',
    bgClass: 'bg-gradient-to-r from-blue-100 to-blue-200 text-blue-800 border-blue-300',
    simpleClass: 'bg-blue-100 text-blue-800',
    buttonActiveClass:
      'bg-gradient-to-r from-blue-500 to-blue-600 text-white border-blue-300 shadow-md',
    buttonInactiveClass:
      'bg-white border-blue-200 text-blue-700 hover:border-blue-300 hover:bg-blue-50',
  },
  'on-the-way': {
    text: 'Yolda',
    icon: FaTruck,
    color: 'purple',
    bgClass: 'bg-gradient-to-r from-purple-100 to-purple-200 text-purple-800 border-purple-300',
    simpleClass: 'bg-purple-100 text-purple-800',
    buttonActiveClass:
      'bg-gradient-to-r from-purple-500 to-purple-600 text-white border-purple-300 shadow-md',
    buttonInactiveClass:
      'bg-white border-purple-200 text-purple-700 hover:border-purple-300 hover:bg-purple-50',
  },
  completed: {
    text: 'Tamamlandı',
    icon: FaCheckCircle,
    color: 'green',
    bgClass: 'bg-gradient-to-r from-green-100 to-green-200 text-green-800 border-green-300',
    simpleClass: 'bg-green-100 text-green-800',
    buttonActiveClass:
      'bg-gradient-to-r from-green-500 to-green-600 text-white border-green-300 shadow-md',
    buttonInactiveClass:
      'bg-white border-green-200 text-green-700 hover:border-green-300 hover:bg-green-50',
  },
  cancelled: {
    text: 'İptal Edildi',
    icon: FaTimesCircle,
    color: 'red',
    bgClass: 'bg-gradient-to-r from-red-100 to-red-200 text-red-800 border-red-300',
    simpleClass: 'bg-red-100 text-red-800',
    buttonActiveClass:
      'bg-gradient-to-r from-red-500 to-red-600 text-white border-red-300 shadow-md',
    buttonInactiveClass:
      'bg-white border-red-200 text-red-700 hover:border-red-300 hover:bg-red-50',
  },
};

export const getStatusText = (status: OrderStatus): string => {
  return ORDER_STATUS_CONFIG[status]?.text || status;
};

export const getStatusIcon = (status: OrderStatus): React.ComponentType<any> => {
  return ORDER_STATUS_CONFIG[status]?.icon || FaClock;
};

export const getStatusIconElement = (status: OrderStatus): React.ReactElement => {
  const IconComponent = getStatusIcon(status);
  const colorClass = getStatusIconColor(status);
  return React.createElement(IconComponent, { className: colorClass });
};

export const getStatusIconColor = (status: OrderStatus): string => {
  switch (status) {
    case 'pending':
      return 'text-yellow-600';
    case 'preparing':
      return 'text-blue-600 animate-spin';
    case 'on-the-way':
      return 'text-purple-600';
    case 'completed':
      return 'text-green-600';
    case 'cancelled':
      return 'text-red-600';
    default:
      return 'text-gray-600';
  }
};

export const getStatusColor = (status: OrderStatus): string => {
  return ORDER_STATUS_CONFIG[status]?.color || 'gray';
};

export const getStatusBgClass = (status: OrderStatus): string => {
  return ORDER_STATUS_CONFIG[status]?.bgClass || 'bg-gray-100 text-gray-800';
};

export const getStatusSimpleClass = (status: OrderStatus): string => {
  return ORDER_STATUS_CONFIG[status]?.simpleClass || 'bg-gray-100 text-gray-800';
};

export const getStatusChangeConfig = (newStatus: OrderStatus, orderId: number) => {
  const config = ORDER_STATUS_CONFIG[newStatus];
  const IconComponent = config.icon;

  return {
    title: `Sipariş ${config.text} Durumuna Geçirilsin mi?`,
    message: `Sipariş #${orderId} durumunu "${config.text}" olarak güncellemek istediğinizden emin misiniz?`,
    confirmText: `Evet, ${config.text}`,
    cancelText: 'İptal',
    type: newStatus === 'cancelled' ? 'danger' : newStatus === 'completed' ? 'info' : 'warning',
    icon: React.createElement(IconComponent),
  } as const;
};

export const formatOrderDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('tr-TR');
};

export const formatOrderTime = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
};

export const formatOrderDateTime = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const formatPrice = (price: number | string): string => {
  const numPrice = typeof price === 'string' ? parseFloat(price) : price;
  return `${(numPrice || 0).toFixed(2)} ₺`;
};

export const getDateFilterLabel = (filter: string): string => {
  switch (filter) {
    case 'today':
      return 'Bugün';
    case 'week':
      return 'Bu Hafta';
    case 'month':
      return 'Bu Ay';
    default:
      return 'Tüm Zamanlar';
  }
};

export type DateFilter = 'all' | 'today' | 'week' | 'month';

export const filterOrdersByDate = (orders: any[], dateFilter: DateFilter) => {
  if (dateFilter === 'all') {
    return orders;
  }

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  return orders.filter(order => {
    const orderDate = new Date(order.created_at);
    switch (dateFilter) {
      case 'today':
        return orderDate >= today;
      case 'week':
        const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
        return orderDate >= weekAgo;
      case 'month':
        const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
        return orderDate >= monthAgo;
      default:
        return true;
    }
  });
};

export const filterOrdersByStatus = (orders: any[], statusFilter: string) => {
  if (statusFilter === 'all') {
    return orders;
  }
  return orders.filter(order => order.status === statusFilter);
};

export const filterOrdersBySearch = (orders: any[], searchTerm: string) => {
  if (!searchTerm) {
    return orders;
  }
  return orders.filter(
    order =>
      order.id.toString().includes(searchTerm) ||
      order.items_summary?.toLowerCase().includes(searchTerm.toLowerCase())
  );
};
